import { getBaseURL } from "./env"

/**
 * Storefront SEO helpers.
 *
 * Centralises the canonical/hreflang/lang logic so every `generateMetadata`
 * (and `app/sitemap.ts`) speaks one truth about URL shape and supported locales.
 */

export const SUPPORTED_LOCALES = ["ua", "ru", "en"] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: SupportedLocale = "ua"

/**
 * Mapping from internal countryCode (URL prefix) to BCP 47 language tag
 * used in `<html lang>` and `alternates.languages` keys.
 */
export const LOCALE_TO_BCP47: Record<SupportedLocale, string> = {
  ua: "uk-UA",
  ru: "ru-UA",
  en: "en-US",
}

/**
 * Mapping from internal countryCode to OpenGraph `og:locale` value.
 */
export const LOCALE_TO_OG: Record<SupportedLocale, string> = {
  ua: "uk_UA",
  ru: "ru_UA",
  en: "en_US",
}

/**
 * Short `<html lang>` value (without country) per locale prefix.
 */
export const LOCALE_TO_HTML_LANG: Record<SupportedLocale, string> = {
  ua: "uk-UA",
  ru: "ru-UA",
  en: "en-US",
}

export const isSupportedLocale = (value: string): value is SupportedLocale =>
  (SUPPORTED_LOCALES as readonly string[]).includes(value)

/**
 * Normalise an arbitrary path so it always starts with "/" and has no
 * trailing slash (except the root). Helpful when callers pass paths like
 * "products/foo" or "/products/foo/".
 */
const normalisePath = (path: string): string => {
  if (!path || path === "/") return ""
  const withLeading = path.startsWith("/") ? path : `/${path}`
  return withLeading.length > 1 && withLeading.endsWith("/")
    ? withLeading.slice(0, -1)
    : withLeading
}

/**
 * Build an absolute URL for a given locale + path.
 *
 *   buildLocalisedUrl("ua", "/products/foo") → "https://kvatronix.in.ua/ua/products/foo"
 *   buildLocalisedUrl("ua", "/")             → "https://kvatronix.in.ua/ua"
 */
export const buildLocalisedUrl = (
  locale: SupportedLocale,
  path: string = ""
): string => {
  const base = getBaseURL().replace(/\/$/, "")
  const normalised = normalisePath(path)
  return `${base}/${locale}${normalised}`
}

/**
 * Build a `Metadata.alternates` object: canonical for the current locale +
 * `languages` map for all supported locales (incl. `x-default`).
 *
 * @param countryCode Current locale prefix (e.g. "ua").
 * @param path Path *within* the locale, without the leading `/{locale}`.
 *             Pass empty string for the locale root.
 */
export const buildAlternates = (
  countryCode: string,
  path: string = ""
): {
  canonical: string
  languages: Record<string, string>
} => {
  const locale: SupportedLocale = isSupportedLocale(countryCode)
    ? countryCode
    : DEFAULT_LOCALE

  const languages: Record<string, string> = {}
  for (const l of SUPPORTED_LOCALES) {
    languages[LOCALE_TO_BCP47[l]] = buildLocalisedUrl(l, path)
  }
  languages["x-default"] = buildLocalisedUrl(DEFAULT_LOCALE, path)

  return {
    canonical: buildLocalisedUrl(locale, path),
    languages,
  }
}
